/**
 * Centralised sampling table for analytics events.
 *
 * Keeps the rate decision out of components — they always call `track(...)`
 * and the hook drops the envelope based on this lookup. Default rate is
 * 1.0 (100% emitted); §4.7 of the T3 spec calls out a few categories that
 * should be sampled lower because the gesture is essentially noise
 * (rapid story swipes, etc.).
 *
 * Schema version is the lookup key so the rate stays bound to the wire
 * contract; if a new schema version ships with the same logical name we
 * intentionally re-default to 1.0 until someone re-adds it here.
 */

const RATES: Record<string, number> = {
  // §4.7 + doc 08 §5.2/5.3 — story viewed across HomeFeed RecoStories,
  // BillsScreen alerts, Insights/Wrapped. 50% drop keeps the funnel signal
  // without exploding volume when users swipe rapidly through stories.
  'ariadne.insight_story.viewed.v1': 0.5,
  'cuidari.insight_story.viewed.v1': 0.5,
}

/**
 * `true` => emit; `false` => drop. Default sample rate is 1.0.
 * Calls `randomFn` exactly once per evaluation; tests can stub it via the
 * second arg to assert deterministic drop/keep decisions.
 */
export function shouldSample(
  schemaVersion: string,
  randomFn: () => number = Math.random,
): boolean {
  const rate = RATES[schemaVersion]
  if (rate === undefined || rate >= 1) return true
  if (rate <= 0) return false
  return randomFn() < rate
}

/** Test helper — exposes the active rate so the lookup stays auditable. */
export function getSampleRate(schemaVersion: string): number {
  return RATES[schemaVersion] ?? 1.0
}
